import React, { useEffect, useState } from "react";
import { ScoutingPhotoCategory } from "../../types";
import { MockAPI } from "../../services/api";
import { Button, Card } from "../../components/ui";
import { useAuth } from "../../services/authContext";
import { Plus, Search, Tags } from "lucide-react";
import {usePhotoNavigationGuard} from "../../services/photos/usePhotoNavigationGuard";
import {isAdminOnlyPhotoCategory} from '../../services/scoutingPhotoCategories';
const empty = { name: "", description: "", color: "#426053" };
const managedNames = new Set([
  "asset",
  "project",
  "place",
  "field captures",
]);
export default function PhotoCategorySettings() {
  const { user,privileges } = useAuth();
  const canEdit = !!privileges.scouting?.write;
  const [categories, setCategories] = useState<ScoutingPhotoCategory[]>([]),
    [query, setQuery] = useState(""),
    [selected, setSelected] = useState<string | null>(null),
    [draft, setDraft] = useState(empty),
    [baseline, setBaseline] = useState(JSON.stringify(empty));
  const [editing, setEditing] = useState(false),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [confirmRemove, setConfirmRemove] = useState(false);
  const dirty = JSON.stringify(draft) !== baseline;
  const customCategories = categories.filter(
    (category) => !managedNames.has(category.name.trim().toLowerCase()),
  );
  const managedCategories = categories.filter((category) =>
    managedNames.has(category.name.trim().toLowerCase()),
  );
  usePhotoNavigationGuard(editing && dirty || busy, "Leave without saving category changes?");
  const load = async () => {
    setLoading(true);
    try {
      setCategories(await MockAPI.getScoutingPhotoCategories());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load categories.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const choose = (category?: ScoutingPhotoCategory) => {
    if (dirty && !window.confirm("Discard unsaved category changes?")) return;
    const value = category
      ? {
          name: category.name,
          description: category.description || "",
          color: category.color || "#426053",
        }
      : empty;
    setDraft(value);
    setBaseline(JSON.stringify(value));
    setSelected(category?.category_id || null);
    setEditing(true);
    setConfirmRemove(false);
    setError("");
    setMessage("");
  };
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = draft.name.trim();
    if (!name) return;
    if (
      categories.some(
        (c) =>
          c.category_id !== selected &&
          c.name.trim().toLowerCase() === name.toLowerCase(),
      )
    ) {
      setError("A category with this name already exists.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const value = { ...draft, name, description: draft.description.trim() };
      if (selected) await MockAPI.updateScoutingPhotoCategory(selected, value);
      else await MockAPI.createScoutingPhotoCategory(value);
      setEditing(false);
      setBaseline(JSON.stringify(draft));
      setMessage(selected ? "Category saved." : "Category added.");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save category.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      await MockAPI.deleteScoutingPhotoCategory(selected);
      setEditing(false);
      setDraft(empty);
      setBaseline(JSON.stringify(empty));
      setMessage("Category removed. Photos are kept.");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not remove category.");
    } finally {
      setBusy(false);
      setConfirmRemove(false);
    }
  };
  return (
    <section className="space-y-5">
      <header className="flex justify-between items-start gap-3">
        <div>
          <h3 className="text-xl font-semibold">Category definitions</h3>
          <p className="text-sm text-gray-500 mt-1">
            Maintain categories used by older records. For current photo organization, use Photo tags.
          </p>
        </div>
        <Button onClick={() => choose()} disabled={!canEdit || busy}>
          <Plus size={16} className="mr-2" />
          Create category
        </Button>
      </header>
      {error && (
        <p
          role="alert"
          className="p-3 rounded-lg bg-red-50 text-sm text-red-700"
        >
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="text-sm text-usda-700">
          {message}
        </p>
      )}
      <div className="grid xl:grid-cols-2 gap-5 items-start">
        <Card
          className={`overflow-hidden ${editing ? "hidden xl:block" : " "}`}
        >
          <label className="flex gap-2 items-center border-b p-4">
            <Search size={18} className="text-gray-400" />
            <input
              aria-label="Search categories"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search categories…"
              className="w-full outline-none text-sm"
            />
          </label>
          {loading ? (
            <p role="status" className="p-5 text-sm text-gray-500">
              Loading categories…
            </p>
          ) : (
            <div className="divide-y">
              {customCategories
                .filter((c) =>
                  c.name.toLowerCase().includes(query.toLowerCase()),
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => (
                  <button
                    key={c.category_id}
                    disabled={busy||isAdminOnlyPhotoCategory(c)&&!user?.roles?.includes('Admin')}
                    onClick={() => choose(c)}
                    className={`w-full text-left p-4 flex gap-3 items-center hover:bg-gray-50 ${editing && selected === c.category_id ? "bg-usda-50" : ""}`}
                  >
                    <span
                      className="w-4 h-4 rounded-full border shrink-0"
                      style={{ background: c.color || "#426053" }}
                    />
                    <span className="min-w-0">
                      <span className="block font-medium text-sm">
                        {c.name}
                      </span>
                      {c.description && (
                        <span className="block text-xs text-gray-500 mt-1">
                          {c.description}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              {!customCategories.length && (
                <p className="p-5 text-sm text-gray-500">
                  No custom categories. Use Photo tags for current photo organization.
                </p>
              )}
            </div>
          )}
          {!!managedCategories.length && (
            <details className="border-t p-4 text-sm">
              <summary className="cursor-pointer font-medium text-gray-600">
                App-managed categories
              </summary>
              <p className="text-xs text-gray-500 my-3">
                Automatic linking tags are maintained by the app.
              </p>
              <ul className="space-y-2">
                {managedCategories.map((category) => (
                  <li
                    key={category.category_id}
                    className="text-gray-500 text-xs"
                  >
                    {category.name}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </Card>
        {editing ? (
          <Card className="p-5">
            <form onSubmit={save} className="space-y-5">
              <h3 className="font-semibold">
                {selected ? "Edit category" : "New category"}
              </h3>
              <fieldset disabled={busy || !canEdit} className="space-y-4">
                <label className="block text-sm font-medium">
                  Name
                  <input
                    required
                    maxLength={100}
                    value={draft.name}
                    onChange={(e) =>
                      setDraft({ ...draft, name: e.target.value })
                    }
                    className="block mt-1.5 w-full border rounded-lg p-3"
                  />
                </label>
                <label className="flex items-center justify-between text-sm font-medium">
                  Tag color
                  <input
                    type="color"
                    value={draft.color}
                    onChange={(e) =>
                      setDraft({ ...draft, color: e.target.value })
                    }
                    className="h-10 w-14 rounded border"
                  />
                </label>
                <label className="block text-sm font-medium">
                  Description{" "}
                  <span className="font-normal text-gray-400">(optional)</span>
                  <textarea
                    rows={3}
                    value={draft.description}
                    onChange={(e) =>
                      setDraft({ ...draft, description: e.target.value })
                    }
                    className="mt-1.5 w-full border rounded-lg p-3"
                  />
                </label>
              </fieldset>
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => {
                    if (
                      !dirty ||
                      window.confirm("Discard unsaved category changes?")
                    ) {
                      setEditing(false);
                      setDraft(empty);
                      setBaseline(JSON.stringify(empty));
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={busy || !canEdit || !dirty || !draft.name.trim()}
                >
                  {busy ? "Saving…" : "Save category"}
                </Button>
              </div>
              {selected && canEdit && (
                <div className="border-t pt-4">
                  {confirmRemove ? (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">
                        Remove this category from the library? Existing photos
                        will be kept.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          disabled={busy}
                          onClick={() => setConfirmRemove(false)}
                        >
                          Keep category
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          disabled={busy}
                          onClick={remove}
                        >
                          Remove category
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setConfirmRemove(true)}
                      className="text-sm text-red-700"
                    >
                      Remove category
                    </button>
                  )}
                </div>
              )}
            </form>
          </Card>
        ) : (
          <div className="p-6 text-gray-500 text-sm">
            <Tags size={24} className="mb-3 text-gray-400" />
            Choose a category to edit its name, color, or description.
          </div>
        )}
      </div>
    </section>
  );
}
